import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { extractPathFromUrl } from '@/lib/storage-utils';

type BucketName = 'patient-photos' | 'shift-photos' | 'avatars' | 'company-logos';

// In-memory cache for signed URLs to avoid duplicate requests
// Key: original URL, Value: { signedUrl, expiresAt }
const signedUrlCache = new Map<string, { signedUrl: string; expiresAt: number }>();

// Pending requests map to deduplicate simultaneous requests for the same URL
// Key: original URL, Value: Promise that resolves to signed URL
const pendingRequests = new Map<string, Promise<string | null>>();

// Clean up expired cache entries periodically
const cleanupCache = () => {
  const now = Date.now();
  for (const [key, value] of signedUrlCache.entries()) {
    if (value.expiresAt < now) {
      signedUrlCache.delete(key);
    }
  }
};

// Run cleanup every 5 minutes
setInterval(cleanupCache, 5 * 60 * 1000);

// Core function to fetch and cache a signed URL (shared by hook and utility)
async function fetchAndCacheSignedUrl(
  url: string,
  bucketName: BucketName,
  expiresIn: number
): Promise<string | null> {
  // Check cache first
  const cached = signedUrlCache.get(url);
  if (cached && cached.expiresAt > Date.now()) {
    return cached.signedUrl;
  }

  // Check if there's already a pending request for this URL
  const pending = pendingRequests.get(url);
  if (pending) {
    return pending;
  }

  // Create a new request promise
  const requestPromise = (async () => {
    try {
      const filePath = extractPathFromUrl(url, bucketName);

      if (!filePath) {
        console.warn('Could not extract path from URL:', url);
        return null;
      }

      const { data, error } = await supabase.storage
        .from(bucketName)
        .createSignedUrl(filePath, expiresIn);

      if (error) {
        console.error('Error creating signed URL:', error);
        return null;
      }

      if (data?.signedUrl) {
        // Cache with TTL slightly less than expiresIn (e.g., 50 min for 1 hour expiry)
        const cacheTtl = Math.max(expiresIn * 0.85, 60) * 1000;
        signedUrlCache.set(url, {
          signedUrl: data.signedUrl,
          expiresAt: Date.now() + cacheTtl
        });
        return data.signedUrl;
      }

      return null;
    } finally {
      // Remove from pending after completion
      pendingRequests.delete(url);
    }
  })();

  // Store the pending promise
  pendingRequests.set(url, requestPromise);

  return requestPromise;
}

/**
 * Hook to get a signed URL for a storage object
 * Returns null if no URL provided or if the user is not authenticated
 * Uses in-memory cache and request deduplication to avoid duplicate requests
 */
export function useSignedUrl(
  url: string | null | undefined,
  bucketName: BucketName,
  expiresIn: number = 3600 // 1 hour default
): string | null {
  const [signedUrl, setSignedUrl] = useState<string | null>(() => {
    // Check cache on initial render
    if (!url) return null;
    const cached = signedUrlCache.get(url);
    if (cached && cached.expiresAt > Date.now()) {
      return cached.signedUrl;
    }
    return null;
  });

  useEffect(() => {
    if (!url) {
      setSignedUrl(null);
      return;
    }

    let isMounted = true;

    fetchAndCacheSignedUrl(url, bucketName, expiresIn).then((result) => {
      if (isMounted) {
        setSignedUrl(result);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [url, bucketName, expiresIn]);

  return signedUrl;
}

/**
 * Utility function to get a signed URL (for use outside of React components)
 * Uses in-memory cache and request deduplication to avoid duplicate requests
 */
export async function getSignedStorageUrl(
  url: string | null | undefined,
  bucketName: BucketName,
  expiresIn: number = 3600
): Promise<string | null> {
  if (!url) return null;
  return fetchAndCacheSignedUrl(url, bucketName, expiresIn);
}

/**
 * Batch get signed URLs for multiple files
 */
export async function getSignedStorageUrls(
  urls: (string | null | undefined)[],
  bucketName: BucketName,
  expiresIn: number = 3600
): Promise<Map<string, string>> {
  const result = new Map<string, string>();

  const validUrls = urls.filter((u): u is string => !!u);

  const promises = validUrls.map(async (url) => {
    const signedUrl = await getSignedStorageUrl(url, bucketName, expiresIn);
    if (signedUrl) {
      result.set(url, signedUrl);
    }
  });

  await Promise.all(promises);

  return result;
}
